import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { blogPostsData } from './blog-posts';

const blogPosts = Object.entries(blogPostsData)
  .map(([id, post]) => ({
    id,
    title: post.title,
    excerpt: post.excerpt,
    date: post.date,
    readTime: post.readTime,
    category: post.category,
    image: post.image
  }))
  .sort((a, b) => new Date(b.date) - new Date(a.date));

function Blog() {
  return (
    <>
      <Helmet>
        <title>Blog - Nutrition Insights & Healthy Eating Tips | FoodLens AI</title>
        <meta name="description" content="Expert insights on nutrition, ingredient analysis, and healthy eating tips to help you make informed food choices." />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Hero */}
        <section className="bg-gradient-hero py-16 md:py-24">
          <div className="section text-center">
            <h1 className="section-heading">FoodLens Blog</h1>
            <p className="section-subheading mx-auto">
              Expert insights on nutrition, ingredient analysis, and healthy eating tips.
            </p>
          </div>
        </section>

        {/* Blog Grid */}
        <section className="section py-16">
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {blogPosts.map((post, index) => (
              <article
                key={post.id}
                className="bg-surface rounded-3xl shadow-soft overflow-hidden hover:shadow-elevated transition-all duration-300 hover:-translate-y-1 animate-fade-in-up"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {/* Image */}
                <div className="h-48 bg-sage-100 relative overflow-hidden">
                  {/* Fallback placeholder - behind image */}
                  <div className="absolute inset-0 bg-gradient-to-br from-sage-200 to-sage-300 flex items-center justify-center">
                    <span className="text-sage-600 font-semibold">{post.category}</span>
                  </div>
                  {/* Actual image - on top */}
                  <img
                    src={post.image}
                    alt={post.title}
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Meta */}
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                    <span className="bg-sage-100 text-sage-700 px-2 py-1 rounded-lg text-xs font-medium">
                      {post.category}
                    </span>
                    <span>•</span>
                    <time dateTime={post.date}>
                      {new Date(post.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </time>
                    <span>•</span>
                    <span>{post.readTime}</span>
                  </div>

                  {/* Title */}
                  <h2 className="font-display text-xl text-charcoal mb-3">
                    <Link
                      to={`/blog/${post.id}`}
                      className="hover:text-sage-600 transition-colors"
                    >
                      {post.title}
                    </Link>
                  </h2>

                  {/* Excerpt */}
                  <p className="text-gray-500 text-sm leading-relaxed mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>

                  {/* Read More */}
                  <Link
                    to={`/blog/${post.id}`}
                    className="inline-flex items-center text-sage-600 hover:text-sage-700 font-medium text-sm transition-colors"
                  >
                    Read article
                    <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Newsletter */}
        <section className="section py-16">
          <div className="bg-sage-100 rounded-3xl p-8 md:p-12 text-center">
            <h3 className="font-display text-2xl md:text-3xl text-charcoal mb-3">
              Stay Updated
            </h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              Get the latest insights on nutrition and healthy eating delivered to your inbox.
            </p>
            <form className="max-w-md mx-auto flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-5 py-3 rounded-xl border border-sage-200 bg-surface text-charcoal placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sage-500 focus:border-transparent"
              />
              <button
                type="submit"
                className="btn-primary"
              >
                Subscribe
              </button>
            </form>
          </div>
        </section>
      </div>
    </>
  );
}

export default Blog;
